import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Package, CreditCard, Truck, CheckCircle, Clock,
  MapPin, ShieldCheck, Timer, Search, Download, Copy, AlertTriangle,
} from 'lucide-react';
import { ordersAPI } from '../services/api';
import { formatPrice, getImageUrl } from '../utils/helpers';
import { toast } from '../hooks/use-toast';

// ── Stepper Steps ──
const STEPPER_LOCAL = [
  { key: 'paid', label: 'Pagado', icon: CreditCard },
  { key: 'searching_driver', label: 'Buscando Repartidor', icon: Search },
  { key: 'driver_assigned', label: 'Asignado', icon: Truck },
  { key: 'in_transit', label: 'En Camino', icon: Truck },
  { key: 'delivered_local', label: 'Entregado', icon: CheckCircle },
];
const STEPPER_NACIONAL = [
  { key: 'paid', label: 'Pagado', icon: CreditCard },
  { key: 'accepted_by_seller', label: 'Preparando', icon: Package },
  { key: 'shipped', label: 'Enviado', icon: Truck },
  { key: 'delivered_national', label: 'Entregado', icon: CheckCircle },
];
const STEPPER_GENERIC = [
  { key: 'pending_payment', label: 'Pendiente', icon: Clock },
  { key: 'paid', label: 'Pagado', icon: CreditCard },
  { key: 'shipped', label: 'Enviado', icon: Truck },
  { key: 'delivered', label: 'Entregado', icon: CheckCircle },
];

const getSteps = (order) => {
  if (order.delivery_type === 'local') return STEPPER_LOCAL;
  if (order.delivery_type === 'nacional') return STEPPER_NACIONAL;
  return STEPPER_GENERIC;
};

// ── Stepper Component ──
const LogisticsStepper = ({ order }) => {
  const steps = getSteps(order);
  const currentIdx = steps.findIndex(s => s.key === order.status);

  return (
    <div className="flex items-center w-full gap-1" data-testid="user-logistics-stepper">
      {steps.map((step, i) => {
        const Icon = step.icon;
        const done = i <= currentIdx;
        const active = i === currentIdx;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center min-w-0 flex-1">
              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                done ? (active ? 'bg-orange-500 border-orange-500 scale-110' : 'bg-green-600 border-green-600') : 'bg-gray-100 border-gray-300'
              }`}>
                {done && !active ? <CheckCircle className={`w-4 h-4 sm:w-5 sm:h-5 text-white`} /> : <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${done ? 'text-white' : 'text-gray-400'}`} />}
              </div>
              <p className={`text-[9px] sm:text-[10px] mt-1 text-center leading-tight ${done ? 'text-gray-900 font-semibold' : 'text-gray-400'}`}>
                {step.label}
              </p>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-0.5 flex-1 mt-[-14px] rounded-full ${i < currentIdx ? 'bg-green-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── Countdown Timer ──
const CountdownTimer = ({ startedAt }) => {
  const [remaining, setRemaining] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!startedAt) return;
    const calc = () => {
      const start = new Date(startedAt).getTime();
      const deadline = start + 5 * 60 * 1000;
      setRemaining(Math.max(0, deadline - Date.now()));
    };
    calc();
    intervalRef.current = setInterval(calc, 1000);
    return () => clearInterval(intervalRef.current);
  }, [startedAt]);

  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  const expired = remaining <= 0;

  return (
    <div className={`flex items-center justify-center gap-3 p-4 rounded-xl border ${
      expired ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'
    }`} data-testid="user-countdown-timer">
      <Timer className={`w-5 h-5 ${expired ? 'text-red-500' : 'text-orange-500'}`} />
      <div className="text-center">
        <p className={`text-2xl font-black font-mono ${expired ? 'text-red-600' : 'text-orange-600'}`}>
          {expired ? '00:00' : `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`}
        </p>
        <p className="text-xs text-gray-500">{expired ? 'Asignando repartidor...' : 'Buscando repartidor cercano'}</p>
      </div>
    </div>
  );
};

// ── PIN Display Component ──
const PinDisplay = ({ code, label, instruction, color = 'orange' }) => {
  const colorClasses = {
    orange: 'bg-orange-50 border-orange-300 text-orange-600',
    cyan: 'bg-cyan-50 border-cyan-300 text-cyan-600',
    amber: 'bg-amber-50 border-amber-300 text-amber-600',
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    toast({ title: `PIN ${code} copiado` });
  };

  return (
    <div className={`rounded-xl border-2 p-5 text-center ${colorClasses[color]}`} data-testid={`pin-display-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-80" />
      <p className="text-xs font-bold uppercase tracking-wider opacity-70 mb-2">{label}</p>
      <p className="text-4xl font-black font-mono tracking-[0.3em]">{code || '----'}</p>
      <p className="text-xs mt-3 opacity-70 max-w-xs mx-auto">{instruction}</p>
      {code && (
        <Button variant="ghost" size="sm" onClick={handleCopy} className="mt-2 text-xs opacity-60 hover:opacity-100">
          <Copy className="w-3 h-3 mr-1" /> Copiar
        </Button>
      )}
    </div>
  );
};


const UserOrderDetail = ({ viewAs = 'buyer' }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await ordersAPI.getById(orderId);
        setOrder(data);
      } catch {
        toast({ title: 'Error cargando orden', variant: 'destructive' });
      } finally { setLoading(false); }
    };
    if (user) load();
  }, [orderId, user]);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-4 text-center">
        <p className="text-gray-600 mb-4">Inicia sesion para ver esta orden</p>
        <Button onClick={() => navigate('/login')}>Iniciar sesion</Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Package className="w-12 h-12 text-gray-300 animate-pulse" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-4 text-center">
        <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <p className="text-xl font-semibold mb-2">Orden no encontrada</p>
        <Button variant="outline" onClick={() => navigate(-1)}>Volver</Button>
      </div>
    );
  }

  const isLocal = order.delivery_type === 'local';
  const isNacional = order.delivery_type === 'nacional';
  const isSearching = order.status === 'searching_driver';
  const isInTransit = order.status === 'in_transit';
  const isBuyer = viewAs === 'buyer';

  const statusLabel = {
    pending_payment: 'Pendiente de Pago', paid: 'Pagado',
    accepted_by_seller: 'Preparando envio', searching_driver: 'Buscando repartidor',
    driver_assigned: 'Repartidor asignado', in_transit: 'En camino',
    shipped: 'Enviado', delivered_local: 'Entregado', delivered_national: 'Entregado',
    delivered: 'Entregado', cancelled: 'Cancelado',
  }[order.status] || order.status;

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="user-order-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-800">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold">
            {isBuyer ? 'Detalle de Compra' : 'Detalle de Venta'}
          </h1>
          <p className="text-sm text-gray-500">
            #{(order.order_id || orderId).slice(0, 8).toUpperCase()} · {new Date(order.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-orange-600">{formatPrice(order.total)}</p>
          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">{statusLabel}</span>
        </div>
      </div>

      {/* Stepper */}
      {order.status !== 'pending_payment' && order.status !== 'cancelled' && (
        <Card className="p-5">
          <LogisticsStepper order={order} />
          {isSearching && order.driver_search_started_at && (
            <div className="mt-4">
              <CountdownTimer startedAt={order.driver_search_started_at} />
            </div>
          )}
        </Card>
      )}

      {/* Pending Payment Info (P2P) */}
      {order.status === 'pending_payment' && order.payment_info?.method === 'p2p' && (
        <Card className="p-5 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800">Pago pendiente</p>
              <p className="text-sm text-amber-700 mt-1">Realiza la transferencia y espera la aprobacion del vendedor/admin.</p>
              {order.payment_info?.bank_name && (
                <div className="mt-3 bg-white rounded-lg p-3 text-sm space-y-1">
                  {order.payment_info.bank_name && <p>Banco: <strong>{order.payment_info.bank_name}</strong></p>}
                  {order.payment_info.account_holder && <p>Titular: <strong>{order.payment_info.account_holder}</strong></p>}
                  {order.payment_info.phone_number && <p>Tel: <strong>{order.payment_info.phone_number}</strong></p>}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ═══ BUYER: Delivery PIN ═══ */}
      {isBuyer && isLocal && (isInTransit || order.status === 'driver_assigned') && (
        <PinDisplay
          code={order.delivery_code}
          label="Codigo de Entrega"
          instruction="Dale este PIN al repartidor al recibir tu paquete"
          color="orange"
        />
      )}

      {/* ═══ SELLER: Pickup PIN ═══ */}
      {!isBuyer && isLocal && ['searching_driver', 'driver_assigned'].includes(order.status) && (
        <PinDisplay
          code={order.pickup_code}
          label="Codigo de Recogida"
          instruction="Dale este PIN al repartidor cuando recoja el paquete"
          color="amber"
        />
      )}

      {/* ═══ SELLER: Download Shipping Label (Nacional) ═══ */}
      {!isBuyer && isNacional && order.shipping_label_url && (
        <Card className="p-5 bg-blue-50 border-blue-200 text-center">
          <Truck className="w-10 h-10 mx-auto text-blue-500 mb-3" />
          <p className="font-semibold text-blue-800 mb-3">Guia de Envio Disponible</p>
          <a href={order.shipping_label_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-lg font-bold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            data-testid="seller-download-label-btn">
            <Download className="w-5 h-5" /> Descargar Guia de Envio (PDF)
          </a>
          {order.tracking_number && (
            <p className="text-sm text-blue-600 mt-3">Guia: <span className="font-mono font-bold">{order.tracking_number}</span></p>
          )}
        </Card>
      )}

      {/* Shipping address */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
          <MapPin className="w-4 h-4 text-gray-400" /> Direccion de envio
        </h3>
        <p className="font-medium">{order.shipping_address?.name}</p>
        <p className="text-sm text-gray-600">{order.shipping_address?.address}</p>
        <p className="text-sm text-gray-600">{order.shipping_address?.city}</p>
        <p className="text-xs text-gray-500">{order.shipping_address?.phone}</p>
      </Card>

      {/* Products */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-gray-400" /> Productos ({order.items?.length || 0})
        </h3>
        <div className="space-y-3">
          {order.items?.map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <img src={getImageUrl(item.product_image)} alt={item.product_title}
                className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80"
                onClick={() => navigate(`/product/${item.product_id}`)}
                onError={e => { e.target.src = 'https://via.placeholder.com/64'; }} />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.product_title}</p>
                {item.variant_label && <p className="text-xs text-gray-500">Variante: {item.variant_label}</p>}
                <p className="text-sm text-gray-500">x{item.quantity} · {item.seller_name}</p>
              </div>
              <p className="font-bold">{formatPrice(item.price * item.quantity)}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t flex justify-between">
          <span className="font-semibold text-gray-700">Total</span>
          <span className="text-xl font-bold text-orange-600">{formatPrice(order.total)}</span>
        </div>
      </Card>

      {/* Tracking info for nacional */}
      {isNacional && order.tracking_number && isBuyer && (
        <Card className="p-5 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3">
            <Truck className="w-6 h-6 text-blue-500" />
            <div>
              <p className="font-semibold text-blue-800">Tu pedido fue enviado</p>
              <p className="text-sm text-blue-600 mt-1">Numero de guia: <span className="font-mono font-bold">{order.tracking_number}</span></p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default UserOrderDetail;
